import { UnifiedToolInfo } from '../types';

// Comprehensive synonym and keyword alias dictionary
const KEYWORD_ALIASES: Record<string, string[]> = {
    // PDF operations
    'viewer': ['pdf-merge', 'pdf-editor', 'pdf-to-image', 'pdf-page-extractor', 'pdf-booklet-maker', 'online-text-editor'],
    'view': ['pdf-merge', 'pdf-editor', 'pdf-to-image'],
    'reader': ['pdf-merge', 'pdf-editor', 'qr-code-reader'],
    'read': ['pdf-merge', 'pdf-editor', 'qr-code-reader', 'voice-to-text'],
    'merge': ['pdf-merge'],
    'merger': ['pdf-merge'],
    'combine': ['pdf-merge', 'image-collage-maker'],
    'join': ['pdf-merge', 'image-collage-maker'],
    'unite': ['pdf-merge'],
    'compress': ['pdf-compress', 'image-compressor'],
    'compressor': ['pdf-compress', 'image-compressor'],
    'shrink': ['pdf-compress', 'image-compressor'],
    'reduce': ['pdf-compress', 'image-compressor', 'image-resizer'],
    'smaller': ['pdf-compress', 'image-compressor', 'image-resizer'],
    'optimizer': ['pdf-compress', 'image-compressor'],
    'split': ['pdf-split', 'pdf-page-extractor', 'instagram-grid-splitter', 'audio-cutter'],
    'splitter': ['pdf-split', 'pdf-page-extractor', 'instagram-grid-splitter', 'audio-cutter'],
    'extract': ['pdf-page-extractor', 'pdf-extract-images', 'youtube-tag-extractor', 'youtube-title-extractor', 'youtube-description-extractor'],
    'watermark': ['pdf-watermark', 'image-watermark'],
    'stamp': ['pdf-watermark', 'image-watermark'],
    'sign': ['pdf-sign'],
    'signature': ['pdf-sign'],
    'password': ['pdf-security', 'random-password-generator'],
    'protect': ['pdf-security'],
    'lock': ['pdf-security'],
    'unlock': ['pdf-unlock'],
    'decrypt': ['pdf-unlock', 'jwt-decoder', 'php-decoder'],
    'encrypt': ['pdf-security', 'php-encoder'],
    'blackout': ['pdf-redactor'],
    'redact': ['pdf-redactor'],
    'hide': ['pdf-redactor', 'image-blur-tool'],
    'blur': ['image-blur-tool'],

    // Barcode & QR
    'barcode': ['barcode-generator'],
    'bar': ['barcode-generator'],
    'upc': ['barcode-generator'],
    'ean': ['barcode-generator'],
    'qr': ['qr-code-generator', 'qr-code-reader'],
    'qrcode': ['qr-code-generator', 'qr-code-reader'],
    'scanner': ['qr-code-reader', 'image-to-text-ocr', 'pdf-ocr'],

    // Image Operations
    'photo': ['image-editor', 'image-compressor', 'image-resizer', 'image-cropper', 'passport-photo-maker', 'free-stock-photo-search'],
    'picture': ['image-editor', 'image-compressor', 'image-resizer', 'image-cropper', 'image-to-pdf'],
    'pic': ['image-editor', 'image-compressor', 'image-resizer', 'image-cropper'],
    'img': ['image-editor', 'image-compressor', 'image-resizer', 'image-cropper', 'image-to-pdf'],
    'background': ['background-remover'],
    'bg': ['background-remover'],
    'removebg': ['background-remover'],
    'transparent': ['background-remover', 'image-to-svg'],
    'cutout': ['background-remover'],
    'upscale': ['image-upscaler', 'image-enhancer'],
    'enhance': ['image-upscaler', 'image-enhancer'],
    'hd': ['image-upscaler', 'image-enhancer'],
    '4k': ['image-upscaler'],
    'collage': ['image-collage-maker'],
    'grid': ['instagram-grid-splitter', 'image-collage-maker'],
    'crop': ['image-cropper', 'pdf-crop'],
    'resize': ['image-resizer', 'pdf-crop'],
    'vector': ['image-to-svg'],
    'svg': ['image-to-svg'],

    // OCR & Speech
    'ocr': ['image-to-text-ocr', 'pdf-ocr', 'pdf-to-text'],
    'transcribe': ['voice-to-text', 'text-to-speech'],
    'speech': ['voice-to-text', 'text-to-speech'],
    'voice': ['voice-to-text', 'text-to-speech', 'audio-cutter'],
    'audio': ['audio-cutter', 'voice-to-text', 'text-to-speech'],
    'mp3': ['audio-cutter', 'voice-to-text'],
    'mp4': ['facebook-downloader', 'instagram-downloader', 'tiktok-downloader', 'twitter-downloader', 'pinterest-downloader'],

    // Converters
    'convert': ['image-to-pdf', 'pdf-to-image', 'doc-to-pdf', 'excel-to-pdf', 'ppt-to-pdf', 'html-to-pdf', 'image-format-converter', 'ico-converter', 'currency', 'time-converter', 'unit-converter'],
    'converter': ['image-to-pdf', 'pdf-to-image', 'doc-to-pdf', 'excel-to-pdf', 'ppt-to-pdf', 'html-to-pdf', 'image-format-converter', 'currency'],
    'word': ['doc-to-pdf', 'word-counter'],
    'doc': ['doc-to-pdf'],
    'docx': ['doc-to-pdf'],
    'excel': ['excel-to-pdf', 'csv-excel-processor', 'csv-to-json'],
    'sheet': ['excel-to-pdf', 'csv-excel-processor'],
    'ppt': ['ppt-to-pdf'],
    'powerpoint': ['ppt-to-pdf'],

    // Social Video Downloaders
    'youtube': ['youtube-thumbnail-downloader', 'youtube-trend', 'youtube-tag-extractor', 'youtube-tag-generator', 'youtube-title-generator'],
    'fb': ['facebook-downloader'],
    'facebook': ['facebook-downloader'],
    'insta': ['instagram-downloader', 'instagram-grid-splitter'],
    'instagram': ['instagram-downloader', 'instagram-grid-splitter'],
    'tiktok': ['tiktok-downloader'],
    'twitter': ['twitter-downloader', 'tweet-to-image'],
    'x': ['twitter-downloader', 'tweet-to-image'],
    'pinterest': ['pinterest-downloader'],
    'video': ['facebook-downloader', 'instagram-downloader', 'tiktok-downloader', 'twitter-downloader', 'pinterest-downloader', 'youtube-thumbnail-downloader'],
    'download': ['facebook-downloader', 'instagram-downloader', 'tiktok-downloader', 'twitter-downloader', 'pinterest-downloader', 'youtube-thumbnail-downloader', 'free-stock-photo-search'],
    'downloader': ['facebook-downloader', 'instagram-downloader', 'tiktok-downloader', 'twitter-downloader', 'pinterest-downloader', 'youtube-thumbnail-downloader'],

    // Calculators
    'calc': ['age-calculator', 'bmi-calculator', 'loan-calculator', 'percentage-calculator', 'freelance-rate-calculator', 'trip-fuel-calculator', 'gst-calculator', 'sales-tax-calculator', 'sleep-calculator'],
    'calculator': ['age-calculator', 'bmi-calculator', 'loan-calculator', 'percentage-calculator', 'freelance-rate-calculator', 'trip-fuel-calculator', 'gst-calculator', 'sales-tax-calculator', 'sleep-calculator'],
    'age': ['age-calculator', 'sleep-calculator'],
    'loan': ['loan-calculator'],
    'emi': ['loan-calculator'],
    'interest': ['loan-calculator', 'percentage-calculator'],
    'tax': ['gst-calculator', 'sales-tax-calculator'],
    'gst': ['gst-calculator'],
    'fuel': ['trip-fuel-calculator'],
    'trip': ['trip-fuel-calculator'],
    'rate': ['freelance-rate-calculator', 'currency'],
    'money': ['currency', 'loan-calculator', 'freelance-rate-calculator', 'gst-calculator'],
    'dollar': ['currency'],
    'crypto': ['hash-generator', 'duplicate-file-finder'],

    // Dev & Formatter
    'format': ['json-formatter', 'html-beautifier', 'image-format-converter'],
    'formatter': ['json-formatter', 'html-beautifier'],
    'beautify': ['html-beautifier', 'json-formatter'],
    'json': ['json-formatter', 'xml-to-json', 'csv-to-json', 'tsv-to-json'],
    'xml': ['xml-to-json'],
    'csv': ['csv-to-json', 'csv-excel-processor'],
    'jwt': ['jwt-decoder'],
    'token': ['jwt-decoder'],
    'curl': ['curl-converter'],
    'cron': ['cron-generator'],
    'regex': ['regex-tester'],
    'diff': ['diff-checker'],
    'compare': ['diff-checker'],
};

function normalizeString(str: string): string {
    return str
        .toLowerCase()
        .replace(/[-_\\/.]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function searchTools(allTools: UnifiedToolInfo[], rawQuery: string): UnifiedToolInfo[] {
    const cleanQuery = normalizeString(rawQuery);
    if (!cleanQuery) return allTools;

    const queryTokens = cleanQuery.split(' ').filter(token => token.length > 0);
    if (queryTokens.length === 0) return allTools;

    const scoredTools: { tool: UnifiedToolInfo; score: number }[] = [];

    for (const tool of allTools) {
        const normLabel = normalizeString(tool.label);
        const normId = normalizeString(tool.id);
        const normCategory = normalizeString(tool.category || '');
        const normDesc = normalizeString(tool.description || '');

        let score = 0;
        let matchedTokensCount = 0;

        // 1. Exact or startsWith phrase matches (Ultra High priority)
        if (normLabel === cleanQuery || normId === cleanQuery) {
            score += 200;
        } else if (normLabel.startsWith(cleanQuery) || normId.startsWith(cleanQuery)) {
            score += 120;
        } else if (normLabel.includes(cleanQuery)) {
            score += 90;
        }

        // 2. Token-by-token matching across all fields & synonyms
        for (const token of queryTokens) {
            let tokenMatched = false;

            // Direct label / id match
            if (normLabel.includes(token)) {
                score += 40;
                tokenMatched = true;
            }
            if (normId.includes(token)) {
                score += 35;
                tokenMatched = true;
            }

            // Category match
            if (normCategory.includes(token)) {
                score += 20;
                tokenMatched = true;
            }

            // Description match
            if (normDesc.includes(token)) {
                score += 15;
                tokenMatched = true;
            }

            // Keyword alias / synonym match
            const aliasMatches = KEYWORD_ALIASES[token];
            if (aliasMatches && (aliasMatches.includes(tool.id) || aliasMatches.some(alias => normId.includes(alias)))) {
                score += 45;
                tokenMatched = true;
            } else {
                // Partial alias match (e.g. "viewers" matching "viewer")
                for (const [aliasKey, targetIds] of Object.entries(KEYWORD_ALIASES)) {
                    if (token.startsWith(aliasKey) || aliasKey.startsWith(token)) {
                        if (targetIds.includes(tool.id) || targetIds.some(target => normId.includes(target))) {
                            score += 30;
                            tokenMatched = true;
                            break;
                        }
                    }
                }
            }

            if (tokenMatched) {
                matchedTokensCount++;
            }
        }

        // Boost tools that matched all or most query tokens
        if (matchedTokensCount === queryTokens.length) {
            score += 60; // Perfect multi-word match
        } else if (matchedTokensCount > 0) {
            score += (matchedTokensCount / queryTokens.length) * 30;
        }

        if (score > 0) {
            scoredTools.push({ tool, score });
        }
    }

    // Sort by highest score first
    scoredTools.sort((a, b) => b.score - a.score);

    return scoredTools.map(item => item.tool);
}
